<script lang="ts" setup>
  import { z } from 'zod'
  import type GInputField from '~/components/form/GInputField/GInputField.vue'
  import { PASSWORD_LENGTH_ERROR_MESSAGE } from '~/constants/errorMessage'
  import { useNativeHaptics } from '~/composables/nativeHaptics'

  const props = defineProps<{
    message: string
    showPasswordInput?: boolean
    onSubmit: (code: string, password?: string) => Promise<void>
    onSuccessfulVerifyCode: () => Promise<void>
    onSignOut: () => void
  }>()

  const { alertContent, isAlertVisible } = useGenericAlert()

  const formRef = ref<HTMLFormElement | null>(null)
  const codeInputRef = ref<InstanceType<typeof GInputField> | null>(null)
  const showConfirmSignOutAlert = ref(false)

  const validationSchema = toTypedSchema(
    z.object({
      verificationCode: z
        .string()
        .min(6, 'Please enter a valid 6 digit code')
        .max(6, 'Please enter a valid 6 digit code')
        .regex(/^\d{6}$/, 'Please enter a valid 6 digit code'),
      ...(props.showPasswordInput
        ? {
            password: z.string().min(8, PASSWORD_LENGTH_ERROR_MESSAGE)
          }
        : { password: z.string().min(8, PASSWORD_LENGTH_ERROR_MESSAGE).optional() })
    })
  )

  const { handleSubmit, isSubmitting, meta, values, errors } = useForm({
    validationSchema,
    initialValues: {
      verificationCode: ''
    }
  })

  /**
   * Form is valid if:
   * - Form is dirty (vee-validate)
   * - Form is valid (vee-validate)
   * - No errors (vee-validate)
   */
  const isFormValid = computed(() => {
    return meta.value.dirty && meta.value.valid && !Object.keys(errors.value).length
  })

  const emit = defineEmits(['isFormValid', 'isSubmitting'])

  // Watch isFormValid and emit to parent
  watch(isFormValid, (val) => {
    emit('isFormValid', val)
  })
  // Watch isSubmitting and emit to parent
  watch(isSubmitting, (val) => {
    emit('isSubmitting', val)
  })
  const onSubmit = handleSubmit(async () => {
    isSubmitting.value = true
    try {
      const rawValues = toRaw(values)
      await props.onSubmit(rawValues?.verificationCode ?? '', props.showPasswordInput ? rawValues.password : undefined)

      await props.onSuccessfulVerifyCode()
    } catch (error: any) {
      await useNativeHaptics().triggerVibrate()
      isAlertVisible.value = true
      var message = getApiErrorMessage(error) ?? 'Please check your connection or try again later.'
      var title = getApiErrorTitle(error) ?? 'Oops. Unexpected Error.'
      alertContent.value = {
        header: title,
        message
      }
      console.warn('Error verifying OTP code')
    } finally {
      isSubmitting.value = false
    }
  })

  onMounted(() => {
    setTimeout(() => {
      if (codeInputRef.value) {
        codeInputRef.value.$el.setFocus()
      }
      // Set a slight delay to finish modal enter transition
      // to see keyboard enter animation
    }, 200)

    // Feature detection
    if ('OTPCredential' in window) {
      window.addEventListener('DOMContentLoaded', () => {
        // Cancel the WebOTP API if the form is submitted manually.
        const ac = new AbortController()

        if (formRef.value) {
          formRef.value.addEventListener('submit', () => {
            // Cancel the WebOTP API.
            ac.abort()
          })
        }
        // Invoke the WebOTP API
        navigator.credentials
          .get({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            otp: { transport: ['sms'] },
            signal: ac.signal
          })
          .then((otp) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            values.verificationCode = otp.code
          })
          .catch((err) => {
            console.log(err)
          })
      })
    }
  })

  defineExpose({ onSubmit })
</script>

<template>
  <form ref="formRef" class="verification-form verification-code-field" @submit.prevent.stop="onSubmit">
    <!-- The code input field will be autofilled. This must be placed below the password field in the DOM for the iOS native keyboard autofill to work. -->
    <GInputField
      v-if="props.showPasswordInput"
      id="passwordInput"
      label="Your Password"
      :max-length="50"
      name="password"
      type="password"
      autocomplete="current-password"
    />
    <GInputField
      id="codeInput"
      ref="codeInputRef"
      :max-length="6"
      autocomplete="one-time-code"
      inputmode="numeric"
      type="number"
      label="Verification Code"
      mask-pattern="numeric"
      clear-input
      name="verificationCode"
    />
  </form>
  <div class="body" style="text-align: center">{{ props.message }}</div>

  <template v-if="props.showPasswordInput">
    <GDivider class="divider" color="var(--color-stroke-light)" />
    <div class="body-strong" style="text-align: center">Can’t remember your password?</div>
    <div class="body-small" style="text-align: center">
      No worries.
      <a @click="showConfirmSignOutAlert = true" @keydown.enter="showConfirmSignOutAlert = true">Sign out</a>
      and reset it from the sign in screen. We’ll get you back on track.
    </div>
  </template>

  <GAlert
    :is-open="isAlertVisible"
    :header="alertContent.header"
    :message="alertContent.message"
    @will-dismiss="isAlertVisible = false"
  />
  <GAlert
    :buttons="[
      {
        text: 'Cancel',
        handler: () => (showConfirmSignOutAlert = false),
        cssClass: 'alert-button-secondary'
      },
      {
        text: 'Continue',
        handler: onSignOut
      }
    ]"
    :is-open="showConfirmSignOutAlert"
    header="Are you sure?"
    message="Signing out will remove some of your saved data."
    @did-dismiss="showConfirmSignOutAlert = false"
  />
</template>

<style lang="scss" scoped>
  .verification-form {
    display: flex;
    flex-direction: column;
    row-gap: 12px;
    margin: 6px 0;

    // Reorder the fields so the password field is below the code field
    #passwordInput {
      order: 2;
    }

    #codeInput {
      order: 1;
    }
  }

  .divider {
    margin: 25px 0;
  }
</style>
